import * as React from 'react';
import { cx } from '../utils';
import { JIcon } from '../../icons';
import { devWarn } from '../internal/devWarn';
import { Field } from './Field';
import type { StyledProps } from '../types';
import type { UnsafeStyleProps } from '../escape-hatches';

/**
 * TimePicker — typeable HH:mm field backed by the browser's native
 * `<input type="time">`.
 *
 * Why native: the previous popover + grid + staged-state design
 * surfaced ~13 distinct edge-case bugs across 10 adversarial review
 * iterations (typed-vs-staged conflicts, close-reason routing,
 * partial commits, focus management, bounded-resolve corners,
 * disabled-cell traps, etc.). Each fix was correct in isolation
 * but the state machine grew unmaintainable.
 *
 * The native input gives:
 *   - Free keyboard + accessibility from the browser.
 *   - OS-native time pickers on mobile (excellent UX out of the box).
 *   - Built-in min/max/step enforcement.
 *   - Validation handled by the platform.
 *   - ~100 LOC instead of ~600.
 *
 * Mirrors ADR-005 Tier 1 system-control substitution philosophy
 * (which iOS already does via `DatePicker(.compact)`).
 */

const HHMM_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

function isValidTime(s: string | undefined): s is string {
  return !!s && HHMM_RE.test(s);
}

export interface TimePickerProps extends StyledProps, UnsafeStyleProps {
  /** ISO `HH:mm` 24-hour. */
  value?: string;
  onChange?: (time: string) => void;
  /** Inclusive lower bound `HH:mm`. */
  min?: string;
  /** Inclusive upper bound `HH:mm`. */
  max?: string;
  /** Minute step. Translates to `step={step * 60}` (seconds) on the
   *  native input. */
  step?: 1 | 5 | 15 | 30;
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  error?: boolean;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
  testId?: string;
}

/* Validate min/max with the same parser as `value`. Invalid bounds
   coerce to undefined + dev-warn — passing a malformed bound to the
   native input would silently disable any time it can't parse. */
const _seenBadBoundsRef: Set<string> = new Set();
function safeBound(s: string | undefined, label: string): string | undefined {
  if (s === undefined || s === '') return undefined;
  if (isValidTime(s)) return s;
  const key = `${label}:${s}`;
  if (!_seenBadBoundsRef.has(key)) {
    _seenBadBoundsRef.add(key);
    devWarn(
      `[JKit.TimePicker] ${label}="${s}" is not ISO HH:mm (24-hour); ` +
        "coerced to undefined so the native input doesn't reject every time.",
    );
  }
  return undefined;
}

export function TimePicker({
  value,
  onChange,
  min,
  max,
  step = 5,
  size = 'md',
  disabled = false,
  className,
  unsafe_style,
  error,
  testId,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
}: TimePickerProps) {
  const validValue = isValidTime(value) ? value : undefined;
  const safeMin = safeBound(min, 'min');
  const safeMax = safeBound(max, 'max');

  /* HH:mm strings compare lexicographically, so a string compare is a correct
     bounds check. The controlled value can sit outside [min,max] (typed, or
     pushed by the parent) — surface that as aria-invalid rather than silently
     accepting it as valid. */
  const outOfRange =
    validValue !== undefined &&
    ((safeMin !== undefined && validValue < safeMin) ||
      (safeMax !== undefined && validValue > safeMax));

  if (value && !validValue) {
    devWarn(
      `[JKit.TimePicker] value "${value}" is not ISO HH:mm; native ` +
        'input will render empty until a valid value is supplied.',
    );
  }

  /* Runtime step guard (same rationale as before — TS narrows but JS
     callers can pass anything; we forward step * 60 to the native
     input, where 0/negative would disable the picker). */
  const ALLOWED: ReadonlyArray<number> = [1, 5, 15, 30];
  const safeStep = ALLOWED.includes(step) ? step : 5;
  if (safeStep !== step) {
    devWarn(`[JKit.TimePicker] step=${step} is not one of 1|5|15|30; coerced to 5.`);
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    /* Native input gives '' for cleared, otherwise HH:mm. Validate
       before emitting so onChange always sees the canonical contract
       (or the empty-clear sentinel). */
    if (next === '') {
      /* A controlled invalid value renders the input empty (value={validValue
         ?? ''}), so there is nothing to clear and no change fires — the
         "clear after invalid" case is unreachable in the controlled model.
         Emit the clear only when a valid value was actually present. */
      if (validValue !== undefined) onChange?.('');
      return;
    }
    if (isValidTime(next)) {
      if (next !== validValue) {
        if ((safeMin && next < safeMin) || (safeMax && next > safeMax)) {
          devWarn(
            `[JKit.TimePicker] "${next}" is outside min/max ` +
              `(${safeMin ?? '–'}…${safeMax ?? '–'}); emitted but flagged aria-invalid.`,
          );
        }
        onChange?.(next);
      }
    }
  };

  return (
    <label
      className={cx(
        'j-time-picker',
        size === 'sm' && 'j-time-picker-sm',
        size === 'lg' && 'j-time-picker-lg',
        className,
      )}
      style={unsafe_style}
      data-jq-id={testId}
    >
      <Field
        prefix={<JIcon intent="clock" size={16} aria-hidden />}
        disabled={disabled}
        size={size}
        className={cx(error && 'j-time-picker-field-error')}
      >
        <input
          type="time"
          className="j-input j-time-picker-input"
          value={validValue ?? ''}
          onChange={handleChange}
          min={safeMin}
          max={safeMax}
          step={safeStep * 60}
          disabled={disabled}
          aria-label={ariaLabelledBy ? undefined : (ariaLabel ?? 'Time')}
          aria-labelledby={ariaLabelledBy}
          aria-describedby={ariaDescribedBy}
          aria-invalid={error || outOfRange || undefined}
        />
      </Field>
    </label>
  );
}
